/**
 * Утилита для склонения ФИО по падежам
 * Использует простые правила для наиболее частых случаев
 */

// Правила склонения мужских фамилий
const MALE_SURNAME_RULES = [
  // Фамилии на -ов, -ев, -ин, -ын
  { pattern: /ов$/, 
    cases: { r: 'ова', d: 'ову', v: 'ова', t: 'овым', p: 'ове' } },
  { pattern: /ев$/, 
    cases: { r: 'ева', d: 'еву', v: 'ева', t: 'евым', p: 'еве' } },
  { pattern: /ин$/, 
    cases: { r: 'ина', d: 'ину', v: 'ина', t: 'иным', p: 'ине' } },
  { pattern: /ын$/, 
    cases: { r: 'ына', d: 'ыну', v: 'ына', t: 'ыным', p: 'ыне' } },
  
  // Фамилии на -ский, -цкий
  { pattern: /ский$/, 
    cases: { r: 'ского', d: 'скому', v: 'ского', t: 'ским', p: 'ском' } },
  { pattern: /цкий$/, 
    cases: { r: 'цкого', d: 'цкому', v: 'цкого', t: 'цким', p: 'цком' } },
    
  // Фамилии на согласную
  { pattern: /[бвгджзклмнпрстфхцчшщ]$/, 
    cases: { r: 'а', d: 'у', v: 'а', t: 'ом', p: 'е' },
    preserveEnding: true },
];

// Правила склонения женских фамилий
const FEMALE_SURNAME_RULES = [
  // Фамилии на -ова, -ева, -ина, -ына
  { pattern: /([оеи]в|[ии]н|ын)а$/, 
    cases: { r: 'ой', d: 'ой', v: 'у', t: 'ой', p: 'ой' } },
  
  // Фамилии на -ская, -цкая
  { pattern: /(ск|цк)ая$/, 
    cases: { r: 'ой', d: 'ой', v: 'ую', t: 'ой', p: 'ой' } },
    
  // Фамилии на согласную (не склоняются)
  { pattern: /[бвгджзклмнпрстфхцчшщ]$/, 
    cases: { r: '', d: '', v: '', t: '', p: '' } },
];

// Специальные исключения для имён
const MALE_NAME_EXCEPTIONS = {
  'Пётр': { r: 'Петра', d: 'Петру', v: 'Петра', t: 'Петром', p: 'Петре' },
  'Петр': { r: 'Петра', d: 'Петру', v: 'Петра', t: 'Петром', p: 'Петре' },
  'Павел': { r: 'Павла', d: 'Павлу', v: 'Павла', t: 'Павлом', p: 'Павле' },
  'Лев': { r: 'Льва', d: 'Льву', v: 'Льва', t: 'Львом', p: 'Льве' },
  'Игорь': { r: 'Игоря', d: 'Игорю', v: 'Игоря', t: 'Игорем', p: 'Игоре' },
};

// Правила склонения мужских имён
const MALE_NAME_RULES = [
  // Имена на -й (Николай, Сергей)
  { pattern: /й$/, 
    cases: { r: 'я', d: 'ю', v: 'я', t: 'ем', p: 'е' } },
    
  // Имена на -а (Никита)
  { pattern: /а$/, 
    cases: { r: (word) => getGenitiveAEnding(word), d: 'е', v: 'у', t: 'ой', p: 'е' } },
    
  // Имена на твёрдую согласную (Иван, Роман, Егор)
  { pattern: /[бвгджзклмнпрстфхцчшщ]$/, 
    cases: { r: 'а', d: 'у', v: 'а', t: 'ом', p: 'е' },
    preserveEnding: true },
  // Имена на мягкий знак
  { pattern: /ь$/, 
    cases: { r: 'я', d: 'ю', v: 'я', t: 'ем', p: 'е' } },
];

// Правила склонения женских имён
const FEMALE_NAME_RULES = [
  // Имена на -а (Анна, Мария)
  { pattern: /а$/, 
    cases: { r: (word) => getGenitiveAEnding(word), d: 'е', v: 'у', t: 'ой', p: 'е' } },
    
  // Имена на -я (Юлия, Анастасия)
  { pattern: /я$/, 
    cases: { r: 'и', d: 'и', v: 'ю', t: 'ей', p: 'и' } },
    
  // Имена на -ь (Любовь)
  { pattern: /ь$/, 
    cases: { r: 'и', d: 'и', v: 'ь', t: 'ью', p: 'и' } },
];

// Правила склонения отчеств
const PATRONYMIC_RULES = [
  // Мужские отчества на -ович, -евич
  { pattern: /ович$/, 
    cases: { r: 'овича', d: 'овичу', v: 'овича', t: 'овичем', p: 'овиче' } },
  { pattern: /евич$/, 
    cases: { r: 'евича', d: 'евичу', v: 'евича', t: 'евичем', p: 'евиче' } },
    
  // Женские отчества на -овна, -евна
  { pattern: /овна$/, 
    cases: { r: 'овны', d: 'овне', v: 'овну', t: 'овной', p: 'овне' } },
  { pattern: /евна$/, 
    cases: { r: 'евны', d: 'евне', v: 'евну', t: 'евной', p: 'евне' } },
];

const RUSSIAN_SEVEN_LETTER_RE = /[гкхжчшщц]а$/i;

function getGenitiveAEnding(word) {
  return RUSSIAN_SEVEN_LETTER_RE.test(String(word || '')) ? 'и' : 'ы';
}

/**
 * Склоняет слово по падежам
 * @param {string} word - исходное слово
 * @param {string} case_name - падеж (r, d, v, t, p)
 * @param {Array} rules - правила склонения
 * @param {Object} exceptions - словарь исключений
 * @returns {string} - склоненное слово
 */
function declineWord(word, case_name, rules, exceptions = {}) {
  if (!word || case_name === 'i') return word; // именительный падеж
  
  // Сначала проверяем исключения
  if (exceptions[word] && exceptions[word][case_name]) {
    return exceptions[word][case_name];
  }
  
  for (const rule of rules) {
    if (rule.pattern.test(word)) {
      const endingRule = rule.cases[case_name];
      const ending = typeof endingRule === 'function'
        ? endingRule(word, case_name)
        : (endingRule || '');
      if (ending === '') return word; // не склоняется
      
      // Убираем совпадающую часть с конца и добавляем новое окончание (если не указано иное)
      const match = word.match(rule.pattern);
      if (match) {
        const matchedPart = match[0];
        const stem = rule.preserveEnding ? word : word.slice(0, -matchedPart.length);
        return stem + ending;
      }
    }
  }
  
  return word; // если правило не найдено, возвращаем как есть
}

/**
 * Определяет пол по отчеству
 * @param {string} patronymic - отчество
 * @returns {string} - 'male' или 'female'
 */
function detectGender(patronymic) {
  const normalized = String(patronymic || '').trim().toLowerCase();
  if (!normalized) return 'male';
  if (/\b(оглы|оглу|улы|уулу)\b/.test(normalized)) return 'male';
  if (/\b(кызы|гызы)\b/.test(normalized)) return 'female';
  if (/(ов|ев)ич$/.test(normalized)) return 'male';
  if (/(ов|ев)на$/.test(normalized)) return 'female';
  return 'male'; // по умолчанию
}

function isTurkicGenderMarker(part) {
  const normalized = String(part || '').trim().toLowerCase();
  return /^(оглы|оглу|улы|уулу|кызы|гызы)$/.test(normalized);
}

function declineHyphenatedWord(word, case_name, declinePart, options = {}) {
  if (!word || case_name === 'i' || !String(word).includes('-')) {
    return declinePart(word, 0, [word]);
  }
  const { preserveGenitiveLikeTail = false } = options;
  const parts = String(word).split('-');
  return parts.map((part, index) => {
    if (
      preserveGenitiveLikeTail
      && case_name === 'r'
      && index > 0
      && /[бвгджзклмнпрстфхцчшщ]а$/i.test(part)
    ) {
      return part;
    }
    return declinePart(part, index, parts);
  }).join('-');
}

function isIndeclinableSurnamePart(part) {
  const lower = String(part || '').trim().toLowerCase();
  if (!lower) return false;
  // Современная норма: фамилии на -ых/-их обычно не склоняются (Высоких, Черных, Долгих).
  // Фамилии на -енко также несклоняемые (Клименко, Шевченко и т.п.).
  return /(ых|их|енко)$/.test(lower);
}

function isFemaleConsonantSurnamePart(part) {
  const lower = String(part || '').trim().toLowerCase();
  if (!lower) return false;
  return /[бвгджзйклмнпрстфхцчшщ]$/.test(lower);
}

/**
 * Склоняет ФИО по падежам
 * @param {string} fullName - полное ФИО
 * @param {string} case_name - падеж: i(менительный), r(одительный), d(ательный), v(инительный), t(ворительный), p(редложный)
 * @returns {string} - склоненное ФИО
 */
export function declineFullName(fullName, case_name) {
  if (!fullName || !case_name) return fullName;
  
  const parts = fullName.trim().split(/\s+/);
  if (parts.length < 2) return fullName;
  
  const surname = parts[0];
  const name = parts[1];
  const tailParts = parts.slice(2);
  const patronymic = tailParts[0] || '';
  const extraTail = tailParts.slice(1);

  // ФИО формата "... ... <имя отца> Кызы/Оглы" считаем не склоняемым по частям,
  // чтобы не ломать нетипичные национальные формы.
  if (tailParts.some(isTurkicGenderMarker)) {
    return [surname, name, ...tailParts].filter(Boolean).join(' ');
  }
  
  // Определяем пол по отчеству (если есть)
  const gender = detectGender(tailParts.join(' '));
  
  const declineFemaleSurnameSpecial = (value, targetCase) => {
    if (!value || targetCase === 'i') return value;
    if (/(ск|цк)ая$/i.test(value)) {
      const stem = value.slice(0, -2); // убираем только "ая", сохраняем "ск/цк"
      const endings = { r: 'ой', d: 'ой', v: 'ую', t: 'ой', p: 'ой' };
      return stem + (endings[targetCase] || '');
    }
    if (/(ова|ева|ина|ына)$/i.test(value)) {
      const stem = value.slice(0, -1);
      const endings = { r: 'ой', d: 'ой', v: 'у', t: 'ой', p: 'ой' };
      return stem + (endings[targetCase] || '');
    }
    return null;
  };

  const declineSurnamePart = (part) => {
    if (isIndeclinableSurnamePart(part)) {
      return part;
    }
    if (gender === 'female' && isFemaleConsonantSurnamePart(part)) {
      return part;
    }
    let declined = gender === 'female'
      ? (declineFemaleSurnameSpecial(part, case_name)
        || declineWord(part, case_name, FEMALE_SURNAME_RULES))
      : declineWord(part, case_name, MALE_SURNAME_RULES);
    if (declined === part) {
      const adjective = declineAdjectiveWord(part, case_name);
      declined = adjective !== part ? adjective : declineRoleNoun(part, case_name);
    }
    return declined;
  };

  const declineNamePart = (part) => {
    let declined = declineWord(
      part,
      case_name,
      gender === 'male' ? MALE_NAME_RULES : FEMALE_NAME_RULES,
      gender === 'male' ? MALE_NAME_EXCEPTIONS : {}
    );
    if (declined === part) {
      const adjective = declineAdjectiveWord(part, case_name);
      declined = adjective !== part ? adjective : declineRoleNoun(part, case_name);
    }
    return declined;
  };

  const declinePatronymicPart = (part) => {
    const declined = declineWord(part, case_name, PATRONYMIC_RULES);
    return declined === part ? declineRoleNoun(part, case_name) : declined;
  };

  const declinedSurname = declineHyphenatedWord(surname, case_name, declineSurnamePart);
  const declinedName = declineHyphenatedWord(name, case_name, declineNamePart, { preserveGenitiveLikeTail: true });
  const declinedPatronymic = patronymic ? declineHyphenatedWord(patronymic, case_name, declinePatronymicPart) : '';
  
  return [declinedSurname, declinedName, declinedPatronymic, ...extraTail]
    .filter(Boolean)
    .join(' ');
}

/**
 * Создает все падежные формы для ФИО
 * @param {string} fullName - полное ФИО в именительном падеже
 * @returns {object} - объект с падежными формами
 */
export function createAllCases(fullName) {
  return {
    i: fullName, // именительный
    r: declineFullName(fullName, 'r'), // родительный
    d: declineFullName(fullName, 'd'), // дательный
    v: declineFullName(fullName, 'v'), // винительный
    t: declineFullName(fullName, 't'), // творительный
    p: declineFullName(fullName, 'p'), // предложный
  };
}

const HARD_ADJECTIVE_ENDINGS = {
  r: 'ого',
  d: 'ому',
  v: 'ого',
  t: 'ым',
  p: 'ом',
};

const SOFT_ADJECTIVE_ENDINGS = {
  r: 'его',
  d: 'ему',
  v: 'его',
  t: 'им',
  p: 'ем',
};

const FEMININE_A_ENDINGS = {
  r: 'ой',
  d: 'ой',
  v: 'ую',
  t: 'ой',
  p: 'ой',
};

const FEMININE_YA_ENDINGS = {
  r: 'ей',
  d: 'ей',
  v: 'юю',
  t: 'ей',
  p: 'ей',
};

const NEUTER_OE_ENDINGS = {
  r: 'ого',
  d: 'ому',
  v: 'ое',
  t: 'ым',
  p: 'ом',
};

const NEUTER_EE_ENDINGS = {
  r: 'его',
  d: 'ему',
  v: 'ее',
  t: 'им',
  p: 'ем',
};

const CONSONANT_NOUN_ENDINGS = {
  r: 'а',
  d: 'у',
  v: 'а',
  t: 'ом',
  p: 'е',
};

const CONSONANT_HUSH_ENDINGS = {
  ...CONSONANT_NOUN_ENDINGS,
  t: 'ем',
};

const SOFT_SIGN_NOUN_ENDINGS = {
  r: 'я',
  d: 'ю',
  v: 'я',
  t: 'ем',
  p: 'е',
};

const VOWEL_NOUN_ENDINGS = {
  а: {
    r: 'ы',
    d: 'е',
    v: 'у',
    t: 'ой',
    p: 'е',
  },
  я: {
    r: 'и',
    d: 'е',
    v: 'ю',
    t: 'ей',
    p: 'е',
  },
  о: {
    r: 'а',
    d: 'у',
    v: 'о',
    t: 'ом',
    p: 'е',
  },
  е: {
    r: 'я',
    d: 'ю',
    v: 'е',
    t: 'ем',
    p: 'е',
  },
};

function preserveCase(original, transformedLower) {
  if (!original) return transformedLower;
  if (!transformedLower) return transformedLower;
  if (original === original.toUpperCase()) {
    return transformedLower.toUpperCase();
  }
  if (original === original.toLowerCase()) {
    return transformedLower;
  }
  if (original[0] === original[0].toUpperCase()) {
    return transformedLower.charAt(0).toUpperCase() + transformedLower.slice(1);
  }
  return transformedLower;
}

function declineAdjectiveWord(word, case_name) {
  if (!word || case_name === 'i') return word;
  const lower = word.toLowerCase();

  if (lower === 'божий') {
    const special = {
      r: 'божьего',
      d: 'божьему',
      v: 'божьего',
      t: 'божьим',
      p: 'божьем',
    };
    return preserveCase(word, special[case_name] || lower);
  }

  const apply = (stemLength, endings) => {
    const stem = lower.slice(0, -stemLength);
    const resultLower = stem + (endings[case_name] || '');
    return preserveCase(word, resultLower);
  };

  if (/(ый|ой)$/.test(lower)) {
    return apply(2, HARD_ADJECTIVE_ENDINGS);
  }

  if (/ий$/.test(lower)) {
    return apply(2, SOFT_ADJECTIVE_ENDINGS);
  }

  if (/ая$/.test(lower)) {
    return apply(2, FEMININE_A_ENDINGS);
  }

  if (/яя$/.test(lower)) {
    return apply(2, FEMININE_YA_ENDINGS);
  }

  if (/ое$/.test(lower)) {
    return apply(2, NEUTER_OE_ENDINGS);
  }

  if (/ее$/.test(lower)) {
    return apply(2, NEUTER_EE_ENDINGS);
  }

  return word;
}

function declineRoleNoun(word, case_name) {
  if (!word || case_name === 'i') return word;
  const lower = word.toLowerCase();

  const append = (resultLower) => preserveCase(word, resultLower);

  if (/й$/.test(lower)) {
    const stem = lower.slice(0, -1);
    const endings = {
      r: 'я',
      d: 'ю',
      v: 'я',
      t: 'ем',
      p: 'е',
    };
    return append(stem + (endings[case_name] || ''));
  }

  if (/ь$/.test(lower)) {
    const stem = lower.slice(0, -1);
    return append(stem + (SOFT_SIGN_NOUN_ENDINGS[case_name] || ''));
  }

  if (/[аяоеё]$/.test(lower)) {
    const last = lower.slice(-1);
    const scheme = VOWEL_NOUN_ENDINGS[last];
    if (scheme) {
      const stem = lower.slice(0, -1);
      if (last === 'а' && case_name === 'r') {
        return append(stem + getGenitiveAEnding(lower));
      }
      return append(stem + (scheme[case_name] || ''));
    }
    return word;
  }

  if (/(ый|ой|ий|ая|яя)$/.test(lower)) {
    return declineAdjectiveWord(word, case_name);
  }

  if (/[бвгджзклмнпрстфхцчшщ]$/.test(lower)) {
    const endings = /[жчшщ]$/.test(lower) ? CONSONANT_HUSH_ENDINGS : CONSONANT_NOUN_ENDINGS;
    return append(lower + (endings[case_name] || ''));
  }

  return word;
}

function declineRoleWord(word, case_name) {
  if (!word || case_name === 'i') return word;
  const declinedAdjective = declineAdjectiveWord(word, case_name);
  if (declinedAdjective !== word) {
    return declinedAdjective;
  }
  return declineRoleNoun(word, case_name);
}

function normalizeWhitespace(value) {
  return value.replace(/\s+/g, ' ').trim();
}

function declineExecutiveRole(role, case_name) {
  if (!role || case_name === 'i') {
    return role;
  }

  const normalized = normalizeWhitespace(role);
  const parts = normalized.split(' ');
  if (parts.length === 0) return role;

  const headIndex = parts.length - 1;
  const head = parts[headIndex];
  const before = parts.slice(0, headIndex);
  const after = parts.slice(headIndex + 1);

  const declinedBefore = before.map((word) => declineAdjectiveWord(word, case_name));
  const declinedHead = declineRoleWord(head, case_name);

  return [...declinedBefore, declinedHead, ...after].filter(Boolean).join(' ');
}

export function createExecutiveRoleCases(roleTitle) {
  const base = (roleTitle || '').trim();
  if (!base) {
    return {
      i: '',
      r: '',
      d: '',
      v: '',
      t: '',
      p: '',
    };
  }

  const normalized = normalizeWhitespace(base);
  return {
    i: normalized,
    r: declineExecutiveRole(normalized, 'r'),
    d: declineExecutiveRole(normalized, 'd'),
    v: declineExecutiveRole(normalized, 'v'),
    t: declineExecutiveRole(normalized, 't'),
    p: declineExecutiveRole(normalized, 'p'),
  };
}

function shouldSkipOrganizationWord(wordLower, wordOriginal) {
  if (!wordLower) return true;
  // служебные слова и предлоги
  if (['и', 'в', 'во', 'на', 'по', 'о', 'об', 'от', 'для', 'при', 'к', 'с', 'со', 'из', 'у'].includes(wordLower)) return true;
  // цифры/символы
  if (/\d/.test(wordLower)) return true;
  // уже похоже на косвенный падеж (обычно "хвост" дополнения) — не трогаем
  if (wordOriginal === wordOriginal.toLowerCase()) {
    if (/(ого|его|ому|ему|ыми|ими|ами|ями|ах|ях|ом|ем|ой|ей|ую|юю|ых|их|ов|ев|ёв|ей)$/.test(wordLower)) {
      return true;
    }
  }
  return false;
}

function declineOrganizationWord(word, case_name) {
  if (!word || case_name === 'i') return word;
  const lower = word.toLowerCase();
  if (shouldSkipOrganizationWord(lower, word)) return word;

  // Сначала пробуем как прилагательное
  const adj = declineAdjectiveWord(word, case_name);
  if (adj !== word) return adj;
  // Затем как существительное/прочее
  return declineRoleNoun(word, case_name);
}

function declineOrganizationTextOutsideQuotes(text, case_name) {
  if (!text || case_name === 'i') return text;
  // Склоняем только слова, сохраняя пунктуацию как есть.
  return String(text).replace(/([A-Za-zА-Яа-яЁё]+(?:-[A-Za-zА-Яа-яЁё]+)*)/g, (m) => declineOrganizationWord(m, case_name));
}

export function createOrganizationCasesPreserveQuotes(value) {
  const base = String(value || '');
  if (!base.trim()) {
    return { i: base, r: base, d: base, v: base, t: base, p: base };
  }

  const splitByQuotes = (input) => {
    const parts = [];
    const re = /("([^"\\]|\\.)*"|«[^»]*»)/g;
    let last = 0;
    let m;
    while ((m = re.exec(input)) !== null) {
      if (m.index > last) parts.push({ type: 'text', value: input.slice(last, m.index) });
      parts.push({ type: 'quote', value: m[0] });
      last = m.index + m[0].length;
    }
    if (last < input.length) parts.push({ type: 'text', value: input.slice(last) });
    return parts;
  };

  const segments = splitByQuotes(base);

  const build = (case_name) => segments.map((seg) => {
    if (seg.type === 'quote') return seg.value;
    const raw = seg.value || '';
    const ws = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
    const lead = ws?.[1] || '';
    const mid = ws?.[2] || '';
    const trail = ws?.[3] || '';
    return lead + declineOrganizationTextOutsideQuotes(mid, case_name) + trail;
  }).join('');

  return {
    i: base,
    r: build('r'),
    d: build('d'),
    v: build('v'),
    t: build('t'),
    p: build('p'),
  };
}

export function createSimpleTextCases(value) {
  const base = String(value || '').trim();
  if (!base) {
    return { i: '', r: '', d: '', v: '', t: '', p: '' };
  }

  const parts = base.split(/\s+/);
  const head = parts[0];
  const tail = parts.slice(1).join(' ');
  const suffix = tail ? ` ${tail}` : '';

  const declineHead = (case_name) => {
    // declineRoleWord поддерживает прилагательные/существительные и сохраняет регистр
    return declineRoleWord(head, case_name);
  };

  return {
    i: base,
    r: `${declineHead('r')}${suffix}`.trim(),
    d: `${declineHead('d')}${suffix}`.trim(),
    v: `${declineHead('v')}${suffix}`.trim(),
    t: `${declineHead('t')}${suffix}`.trim(),
    p: `${declineHead('p')}${suffix}`.trim(),
  };
}

/**
 * Получает описание падежа для UI
 */
export const CASE_DESCRIPTIONS = {
  i: 'Именительный (кто? что?)',
  r: 'Родительный (кого? чего?)',
  d: 'Дательный (кому? чему?)',
  v: 'Винительный (кого? что?)',
  t: 'Творительный (кем? чем?)',
  p: 'Предложный (о ком? о чём?)'
};
